// Sync Manager - Handles syncing offline data to server
import axios from "axios";
import {
  getSyncQueue,
  removeFromSyncQueue,
  getAllFromStore,
  saveToStore,
  deleteFromStore,
  STORES
} from "./offlineStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export class SyncManager {
  constructor(onSyncStatusChange) {
    this.onSyncStatusChange = onSyncStatusChange || (() => {});
    this.isSyncing = false;
  }

  async syncAll() {
    if (this.isSyncing) {
      console.log("Sync already in progress");
      return;
    }

    this.isSyncing = true;
    this.onSyncStatusChange({ syncing: true, pendingCount: 0 });

    try {
      // Get all queued operations
      const queue = await getSyncQueue();
      
      if (queue.length === 0) {
        console.log("No pending operations to sync");
        this.isSyncing = false;
        this.onSyncStatusChange({ syncing: false, pendingCount: 0 });
        return { success: true, synced: 0 };
      }

      console.log(`Starting sync of ${queue.length} operations`);
      let successCount = 0;
      let failCount = 0;

      // Process each operation in order
      for (const operation of queue) {
        try {
          await this.processOperation(operation);
          await removeFromSyncQueue(operation.id);
          successCount++;
          
          // Update progress
          this.onSyncStatusChange({ 
            syncing: true, 
            pendingCount: queue.length - successCount
          });
        } catch (error) {
          console.error(`Failed to sync operation ${operation.id}:`, error);
          failCount++;
          
          // If it's a 404 or data doesn't exist anymore, remove from queue
          if (error.response && error.response.status === 404) {
            console.log(`Removing stale operation ${operation.id} from queue`);
            await removeFromSyncQueue(operation.id);
          }
        }
      }

      // Sync complete - refresh data from server
      await this.refreshFromServer();

      this.isSyncing = false;
      this.onSyncStatusChange({ syncing: false, pendingCount: 0 });

      return { 
        success: failCount === 0, 
        synced: successCount, 
        failed: failCount 
      };
    } catch (error) {
      console.error("Sync failed:", error);
      this.isSyncing = false;
      this.onSyncStatusChange({ syncing: false, error: error.message });
      throw error;
    }
  }

  async processOperation(operation) {
    const { type, entity, data } = operation;

    switch (type) {
      case 'CREATE_ATHLETE':
        return await axios.post(`${API}/athletes`, data);
      
      case 'UPDATE_ATHLETE':
        return await axios.put(`${API}/athletes/${data.id}`, data);
      
      case 'DELETE_ATHLETE':
        return await axios.delete(`${API}/athletes/${data.id}`);
      
      case 'CREATE_RECORD':
        return await axios.post(`${API}/records`, data);
      
      case 'UPDATE_RECORD':
        return await axios.put(`${API}/records/${data.id}`, data);
      
      case 'DELETE_RECORD':
        return await axios.delete(`${API}/records/${data.id}`);
      
      default:
        throw new Error(`Unknown operation type: ${type}`);
    }
  }

  async refreshFromServer() {
    try {
      // Fetch latest athletes from server
      const athletesResponse = await axios.get(`${API}/athletes`);
      const athletes = athletesResponse.data;

      // Update local storage with server data
      for (const athlete of athletes) {
        await saveToStore(STORES.ATHLETES, athlete);
      }

      // Fetch all records (we'll need to get them per athlete in practice)
      // For now, we'll trust the athlete IDs we have
      const localAthletes = await getAllFromStore(STORES.ATHLETES);
      for (const athlete of localAthletes) {
        try {
          const recordsResponse = await axios.get(`${API}/records/athlete/${athlete.id}`);
          const records = recordsResponse.data;
          
          // Update local records
          for (const record of records) {
            await saveToStore(STORES.RECORDS, record);
          }
        } catch (error) {
          console.error(`Failed to fetch records for athlete ${athlete.id}:`, error);
        }
      }

      console.log("Local storage refreshed from server");
    } catch (error) {
      console.error("Failed to refresh from server:", error);
      throw error;
    }
  }

  async getPendingCount() {
    const queue = await getSyncQueue();
    return queue.length;
  }
}

export default SyncManager;
